import type { Bootstrap, EncryptedRecord, Envelope, FileShare, KdfParams, SoundCloudLibrary } from "../types";

const configuredApiBase = import.meta.env.VITE_API_BASE_URL ?? "";
const isLoopback = typeof window !== "undefined"
  && ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
const apiBase = (isLoopback ? "" : configuredApiBase).replace(/\/$/, "");
const requestTimeoutMs = 25_000;
const transferTimeoutMs = 120_000;
let activeLease: { deviceId: string; generation: number } | null = null;

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit, timeoutMs = requestTimeoutMs): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (activeLease) {
    headers.set("X-NKriZ-Device-Id", activeLease.deviceId);
    headers.set("X-NKriZ-Lease-Generation", String(activeLease.generation));
  }
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${apiBase}${path}`, {
      credentials: "include",
      ...init,
      headers,
      signal: controller.signal
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(response.status, data.error ?? "Request failed", data);
    return data as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw new Error("The server did not respond in time");
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

function uploadRequest<T>(path: string, body: unknown, onProgress: (loaded: number, total: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${apiBase}${path}`);
    xhr.withCredentials = true;
    xhr.timeout = transferTimeoutMs;
    xhr.setRequestHeader("Content-Type", "application/json");
    if (activeLease) {
      xhr.setRequestHeader("X-NKriZ-Device-Id", activeLease.deviceId);
      xhr.setRequestHeader("X-NKriZ-Lease-Generation", String(activeLease.generation));
    }
    xhr.upload.onprogress = (event) => onProgress(event.loaded, event.lengthComputable ? event.total : 0);
    xhr.onload = () => {
      let data: unknown = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; } catch { /* preserve the HTTP status below */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, (data as { error?: string }).error ?? "Request failed", data));
    };
    xhr.onerror = () => reject(new Error("The upload connection was interrupted"));
    xhr.ontimeout = () => reject(new Error("The upload stalled for more than two minutes"));
    xhr.onabort = () => reject(new Error("The upload was cancelled"));
    xhr.send(JSON.stringify(body));
  });
}

function downloadRequest<T>(path: string, onProgress: (loaded: number, total: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${apiBase}${path}`);
    xhr.withCredentials = true;
    xhr.timeout = transferTimeoutMs;
    xhr.onprogress = (event) => onProgress(event.loaded, event.lengthComputable ? event.total : 0);
    xhr.onload = () => {
      let data: unknown = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { return reject(new Error("The encrypted download response was incomplete")); }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, (data as { error?: string }).error ?? "Request failed", data));
    };
    xhr.onerror = () => reject(new Error("The download connection was interrupted"));
    xhr.ontimeout = () => reject(new Error("The download stalled for more than two minutes"));
    xhr.onabort = () => reject(new Error("The download was cancelled"));
    xhr.send();
  });
}

type PasswordBundle = { authCredential: string; authSalt: string; passwordEnvelope: Envelope; kdfParams: KdfParams };
export type LeaseResponse = { generation: number; expiresAt: string; ttlMs: number };

export const api = {
  setLease: (lease: { deviceId: string; generation: number } | null) => { activeLease = lease; },
  bootstrap: () => request<Bootstrap>("/api/v1/bootstrap"),
  setup: (body: PasswordBundle & { username: string; recoveryCredential: string; recoveryEnvelope: Envelope }) =>
    request<{ ok: true }>("/api/v1/setup", { method: "POST", body: JSON.stringify(body) }),
  login: (username: string, authCredential: string) =>
    request<{ ok: true }>("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ username, authCredential }) }),
  logout: () => request<{ ok: true }>("/api/v1/auth/logout", { method: "POST" }),
  recover: (body: PasswordBundle & { recoveryCredential: string }) =>
    request<{ ok: true }>("/api/v1/auth/recover", { method: "POST", body: JSON.stringify(body) }),
  changePassword: (body: PasswordBundle) =>
    request<{ ok: true }>("/api/v1/auth/change-password", { method: "POST", body: JSON.stringify(body) }),
  acquireLease: (deviceId: string) =>
    request<LeaseResponse>("/api/v1/lease/acquire", { method: "POST", body: JSON.stringify({ deviceId }) }),
  renewLease: () => request<LeaseResponse>("/api/v1/lease/renew", { method: "POST" }),
  changes: (cursor: number) => request<{
    changes: Array<EncryptedRecord & { seq: number }>;
    cursor: number;
    hasMore: boolean;
    reset?: boolean;
  }>(`/api/v1/sync/changes?cursor=${cursor}`),
  putRecord: (id: string, baseRevision: number, ciphertext: string, deleted = false) =>
    request<{ id: string; revision: number; changedAt: string }>(`/api/v1/records/${id}`, {
      method: "PUT", body: JSON.stringify({ baseRevision, ciphertext, deleted })
    }),
  initBlob: (body: { id: string; byteSize: number; chunkSize: number; chunkCount: number }) =>
    request<{ ok: true }>("/api/v1/blobs", { method: "POST", body: JSON.stringify(body) }),
  putChunk: (id: string, index: number, envelope: Envelope, onProgress?: (loaded: number, total: number) => void) =>
    onProgress
      ? uploadRequest<{ ok: true }>(`/api/v1/blobs/${id}/chunks/${index}`, envelope, onProgress)
      : request<{ ok: true }>(`/api/v1/blobs/${id}/chunks/${index}`, { method: "PUT", body: JSON.stringify(envelope) }, transferTimeoutMs),
  completeBlob: (id: string) => request<{ ok: true }>(`/api/v1/blobs/${id}/complete`, { method: "POST" }),
  getChunk: (id: string, index: number, onProgress?: (loaded: number, total: number) => void) =>
    onProgress
      ? downloadRequest<Envelope>(`/api/v1/blobs/${id}/chunks/${index}?format=envelope`, onProgress)
      : request<Envelope>(`/api/v1/blobs/${id}/chunks/${index}?format=envelope`, undefined, transferTimeoutMs),
  deleteBlob: (id: string) => request<{ ok: true }>(`/api/v1/blobs/${id}`, { method: "DELETE" }),
  backup: () => request<unknown>("/api/v1/backup"),
  shares: () => request<{ shares: FileShare[] }>("/api/v1/shares"),
  initShare: (body: {
    fileName: string; mime: string; byteSize: number; chunkSize: number; chunkCount: number;
    expiresAt: string | null; customToken?: string; password?: string; encrypted?: boolean;
    encryptionSalt?: string; encryptionIv?: string; originalByteSize?: number;
  }) => request<FileShare>("/api/v1/shares", { method: "POST", body: JSON.stringify(body) }),
  putShareChunk: (id: string, index: number, data: string, onProgress?: (loaded: number, total: number) => void) =>
    onProgress
      ? uploadRequest<{ ok: true }>(`/api/v1/shares/${id}/chunks/${index}`, { data }, onProgress)
      : request<{ ok: true }>(`/api/v1/shares/${id}/chunks/${index}`, { method: "PUT", body: JSON.stringify({ data }) }, transferTimeoutMs),
  completeShare: (id: string) => request<FileShare>(`/api/v1/shares/${id}/complete`, { method: "POST" }),
  updateShare: (id: string, body: { customToken?: string; password?: string | null }) =>
    request<FileShare>(`/api/v1/shares/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  rotateShare: (id: string) => request<FileShare>(`/api/v1/shares/${id}/rotate`, { method: "POST" }),
  deleteShare: (id: string) => request<{ ok: true }>(`/api/v1/shares/${id}`, { method: "DELETE" }),
  soundcloudLibrary: () => request<SoundCloudLibrary>("/api/v1/soundcloud/library", undefined, transferTimeoutMs),
  soundcloudSync: () => request<{
    ok: boolean;
    message: string;
    newTracks: number;
    totalTracks?: number | null;
    error?: string;
    status?: unknown;
  }>("/api/v1/soundcloud/sync", { method: "POST" }, 600_000)
};

export function soundcloudAssetUrl(kind: "artwork" | "audio", trackId: string): string {
  return `${apiBase}/api/v1/soundcloud/${kind}/${encodeURIComponent(trackId)}`;
}
